import type { MouseEvent, ReactNode } from 'react';
import { forwardRef, memo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import type { ITheme } from '../../utils/theme';
import { TypographyVariant } from '../../utils/theme';
import Icon, { IconName } from '../icon/icon';
import { IconScale } from '../icon/types';

interface LinkProps {
  label?: string;
  href?: string;
  color?: string;
  iconName?: IconName;
  isUnderlined?: boolean;
  isExternalLink?: boolean;
  isDownload?: boolean;
  isInline?: boolean;
  showIcon?: boolean;
  typography?: TypographyVariant;
  describedBy?: string;
  className?: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  children?: ReactNode;
  // still requires use of TruncatedText component to truncate the text in the children
  isTruncated?: boolean;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      label,
      href,
      color,
      iconName,
      isUnderlined = false,
      isExternalLink = false,
      isDownload = false,
      isInline = false,
      showIcon = true,
      typography = TypographyVariant.bodyMd2,
      describedBy,
      className,
      onClick,
      children,
      isTruncated = false,
    }: LinkProps,
    ref
  ) => {
    const theme = useTheme();
    return (
      <StyledLink
        ref={ref}
        $color={color ?? theme.palette.primary.main}
        $isUnderlined={isUnderlined}
        $isInline={isInline}
        $typographyVariant={typography}
        href={href}
        aria-label={isExternalLink ? `${label} opens in new tab` : label}
        target={isExternalLink ? '_blank' : '_self'}
        download={isDownload}
        rel={isExternalLink ? 'noopener noreferrer' : undefined}
        data-testid={label?.replaceAll(' ', '_')}
        tabIndex={0}
        aria-describedby={describedBy}
        className={className}
        onClick={onClick}
      >
        <ContentWrapper $isTruncated={isTruncated}>
          {children ?? label}
          {(isExternalLink || iconName) && showIcon && (
            <StyledIcon
              name={iconName ?? IconName.ExternalLink}
              scale={IconScale.S}
              color={color ?? theme.palette.primary.main}
              className="link-icon"
              ariaHidden
            />
          )}
        </ContentWrapper>
      </StyledLink>
    );
  }
);

Link.displayName = 'Link';

export default memo(Link);

const StyledLink = styled.a(
  ({
    theme,
    $color,
    $isUnderlined,
    $isInline,
    $typographyVariant,
  }: {
    theme: ITheme;
    $color: string;
    $isUnderlined: boolean;
    $isInline: boolean;
    $typographyVariant: TypographyVariant;
  }) => {
    const typography = theme.typography[$typographyVariant];
    return {
      ...typography,
      cursor: 'pointer',
      bg: 'transparent',
      margin: 0,
      padding: 0,
      color: $color,
      background: 'none',
      border: 'none',
      display: $isInline ? 'inline-flex' : 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 'fit-content',
      textDecoration: 'none',
      boxShadow: $isUnderlined
        ? `0px 1px 0px 0px ${$color}`
        : `0px 1px 0px 0px transparent`,
      ':hover': {
        boxShadow: `0px 2px 0px 0px ${$color}`,
        transition: 'box-shadow 0.2s linear',
      },
      ':focus-visible': {
        boxShadow: `0px 2px 0px 0px ${$color}`,
        outline: `2px solid ${theme.palette.focus}`,
        borderRadius: theme.spacing(0.25),
      },
    };
  }
);

const ContentWrapper = styled.span<{ $isTruncated: boolean }>`
  ${({ theme, $isTruncated }) => css`
    white-space: nowrap;
    display: flex;
    align-items: center;
    line-height: 1rem;
    height: 1rem;
    gap: ${theme.spacing(0.5)};
    ${$isTruncated &&
    css`
      white-space: normal;
      text-align: left;
    `}
  `}
`;

const StyledIcon = styled(Icon)`
  display: flex;
  align-items: center;
  justify-content: center;
`;
